import i18next from 'i18next';

import { CreateAnnouncementData, ReorderAnnouncementData, UpdateAnnouncementData } from '@breathelife/types';

import { announcementsSlice } from './AnnouncementsSlice';
import { notificationSlice } from '../../Notification/NotificationSlice';
import { Dispatch } from '../../types';
import ApiService from '../../../Services/ApiService';

export const fetchAnnouncements =
  (options?: { includeArchived?: boolean }) =>
  async (dispatch: Dispatch): Promise<void> => {
    dispatch(announcementsSlice.actions.setIsFetching(true));

    try {
      const response = await ApiService.admin.fetchAnnouncements(options);
      dispatch(announcementsSlice.actions.setAnnouncements({ announcements: response.data.data }));
    } catch (error: any) {
      dispatch(
        notificationSlice.actions.setError({
          message: i18next.t('notifications.failedToLoadAnnouncements'),
        }),
      );
    } finally {
      dispatch(announcementsSlice.actions.setIsFetching(false));
    }
  };

export const createAnnouncement =
  (data: CreateAnnouncementData, onSuccess?: () => void) =>
  async (dispatch: Dispatch): Promise<void> => {
    dispatch(announcementsSlice.actions.setIsCreating(true));

    try {
      const response = await ApiService.admin.createAnnouncement(data);
      dispatch(announcementsSlice.actions.addAnnouncement({ announcement: response.data }));
      dispatch(
        notificationSlice.actions.setSuccess({
          message: i18next.t('notifications.announcementCreated'),
          autoHideDuration: 3000,
        }),
      );
      onSuccess?.();
    } catch (error: any) {
      dispatch(
        notificationSlice.actions.setError({
          message: i18next.t('notifications.failedToCreateAnnouncement'),
        }),
      );
    } finally {
      dispatch(announcementsSlice.actions.setIsCreating(false));
    }
  };

export const updateAnnouncement =
  (id: string, data: UpdateAnnouncementData, onSuccess?: () => void) =>
  async (dispatch: Dispatch): Promise<void> => {
    dispatch(announcementsSlice.actions.setIsUpdating(true));

    try {
      const response = await ApiService.admin.updateAnnouncement(id, data);
      dispatch(announcementsSlice.actions.updateAnnouncement({ announcement: response.data }));
      dispatch(
        notificationSlice.actions.setSuccess({
          message: i18next.t('notifications.announcementUpdated'),
          autoHideDuration: 3000,
        }),
      );
      onSuccess?.();
    } catch (error: any) {
      dispatch(
        notificationSlice.actions.setError({
          message: i18next.t('notifications.failedToUpdateAnnouncement'),
        }),
      );
    } finally {
      dispatch(announcementsSlice.actions.setIsUpdating(false));
    }
  };

export const deleteAnnouncement =
  (id: string, onSuccess?: () => void) =>
  async (dispatch: Dispatch): Promise<void> => {
    dispatch(announcementsSlice.actions.setIsDeleting(true));

    try {
      await ApiService.admin.deleteAnnouncement(id);
      dispatch(announcementsSlice.actions.removeAnnouncement({ id }));
      dispatch(
        notificationSlice.actions.setSuccess({
          message: i18next.t('notifications.announcementDeleted'),
          autoHideDuration: 3000,
        }),
      );
      onSuccess?.();
    } catch (error: any) {
      dispatch(
        notificationSlice.actions.setError({
          message: i18next.t('notifications.failedToDeleteAnnouncement'),
        }),
      );
    } finally {
      dispatch(announcementsSlice.actions.setIsDeleting(false));
    }
  };

export const reorderAnnouncements =
  (data: ReorderAnnouncementData[], onSuccess?: () => void) =>
  async (dispatch: Dispatch): Promise<void> => {
    dispatch(announcementsSlice.actions.setIsReordering(true));

    try {
      const response = await ApiService.admin.reorderAnnouncements(data);
      dispatch(announcementsSlice.actions.setAnnouncements({ announcements: response.data }));
      dispatch(
        notificationSlice.actions.setSuccess({
          message: i18next.t('notifications.announcementsReordered'),
          autoHideDuration: 3000,
        }),
      );
      onSuccess?.();
    } catch (error: any) {
      dispatch(
        notificationSlice.actions.setError({
          message: i18next.t('notifications.failedToReorderAnnouncements'),
        }),
      );
    } finally {
      dispatch(announcementsSlice.actions.setIsReordering(false));
    }
  };

export const selectAnnouncement =
  (id: string) =>
  async (dispatch: Dispatch): Promise<void> => {
    dispatch(announcementsSlice.actions.setIsFetching(true));

    try {
      const response = await ApiService.admin.fetchAnnouncement(id);
      dispatch(announcementsSlice.actions.setSelectedAnnouncement({ announcement: response.data }));
    } catch (error: any) {
      dispatch(
        notificationSlice.actions.setError({
          message: i18next.t('notifications.failedToLoadAnnouncement'),
        }),
      );
    } finally {
      dispatch(announcementsSlice.actions.setIsFetching(false));
    }
  };

export const clearSelectedAnnouncement =
  () =>
  (dispatch: Dispatch): void => {
    dispatch(announcementsSlice.actions.setSelectedAnnouncement(undefined));
  };
